import React from 'react';
import { Sparkles, MoonStar, Clock3, Wand2, Video, ArrowRightLeft, ArrowUpRight } from 'lucide-react';
import { AskAiSection, ECOSYSTEM_LOGO_SLOT_CLASS, buildAskAiPrompt, buildAskAiProviders, ecosystemLogoClass } from '@rehub/ask-ai';

type RehubApp = { name: string; tagline: string; description: string; url: string; logo: string; current?: boolean };

const REHUB_APPS: RehubApp[] = [
  { name: 'PhotoEdit', tagline: 'AI Photo Editing', description: 'Professional AI photo editing — tone, colour, detail and crop for every listing image.', url: 'https://photoedit.rehub.studio', logo: 'https://photoedit.rehub.studio/photoedit-logo.png' },
  { name: 'HDR Photo', tagline: 'AI HDR Merging', description: 'Merge bracketed exposures into studio-quality HDR, then enhance automatically.', url: 'https://hdrphoto.rehub.studio', logo: 'https://hdrphoto.rehub.studio/hdr-photo-logo.png' },
  { name: 'Photoclear.ai', tagline: 'AI Decluttering', description: 'Remove clutter and distractions instantly with AI to reveal clean, buyer-ready rooms.', url: 'https://photoclear.rehub.studio', logo: 'https://photoclear.rehub.studio/Photoclear Logo HR Trans.png' },
  { name: 'Virtual Staging', tagline: 'AI Virtual Staging', description: 'Transform empty rooms into beautifully furnished interiors in minutes.', url: 'https://staging.rehub.studio', logo: 'https://staging.rehub.studio/virtual-staging-logo-transparent.png' },
  { name: 'ImageMotion', tagline: 'AI Motion', description: 'Turn static listing photos into cinematic property videos automatically.', url: 'https://imagemotion.rehub.studio', logo: 'https://imagemotion.rehub.studio/Logo.webp' },
  { name: 'VideoMotion', tagline: 'AI Video Editing', description: 'Turn raw walkthrough footage into polished, social-ready video edits.', url: 'https://videomotion.rehub.studio', logo: 'https://videomotion.rehub.studio/logo.png' },
  { name: 'PropertyCuts', tagline: 'Video Assembler', description: 'Stitch photos, clips and floor plans into a finished property video.', url: 'https://propertycuts.rehub.studio', logo: 'https://propertycuts.rehub.studio/logo.png' },
  { name: 'Hyper Dusk', tagline: 'AI Day-to-Dusk', description: 'Transform daytime exteriors into stunning dusk shots with AI-powered lighting.', url: 'https://hyperdusk.rehub.studio', logo: 'https://hyperdusk.rehub.studio/hyperdusk-logo-wordmark-alpha.png', current: true },
  { name: 'Screen Record', tagline: 'Guided Recordings', description: 'Record guided 360° tours and screen walkthroughs with voice, camera and a shareable link.', url: 'https://record.rehub.studio', logo: '/screenrecord-logo.png' },
  { name: '360 Tours', tagline: 'Virtual Tours', description: 'Build immersive virtual tours for property listings and developments.', url: 'https://360tours.rehub.studio', logo: 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png' },
  { name: 'Planos', tagline: 'Interactive Floor Plans', description: 'Create interactive floor plans that let buyers explore properties visually.', url: 'https://planos.rehub.studio', logo: 'https://planos.rehub.studio/Planos Logo Red.png' },
  { name: 'Sketchplan', tagline: 'AI Floor Plans', description: 'Turn rough sketches into accurate, professional floor plans.', url: 'https://sketchplan.rehub.studio', logo: 'https://sketchplan.rehub.studio/cropped-Sketchplan-Logo1-removebg-preview.webp' },
];

const ASK_AI_PRODUCT = 'HyperDusk';
const ASK_AI_PROVIDERS = buildAskAiProviders(ASK_AI_PRODUCT);
const ASK_AI_PROMPT = buildAskAiPrompt({
  productName: ASK_AI_PRODUCT,
  siteUrl: 'https://hyperdusk.rehub.studio',
  askAiUrl: 'https://hyperdusk.studio/ask-ai',
  extraFocus:
    'Focus on AI day-to-dusk conversion for property exteriors, when to use twilight shots in listings, and published credit pricing.',
});

const EcosystemGrid: React.FC = () => (
  <div className="max-w-6xl mx-auto mt-28">
    <div className="text-center mb-12">
      <p className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-4">The ReHub Ecosystem</p>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3">One platform. Every property marketing app.</h2>
      <p className="text-slate-400 max-w-2xl mx-auto">Each app is purpose-built for property and works beautifully on its own — or together as one seamless workflow.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {REHUB_APPS.map((app) => (
        <a
          key={app.name}
          href={app.url}
          target={app.current ? undefined : '_blank'}
          rel={app.current ? undefined : 'noopener noreferrer'}
          className="group flex flex-col rounded-2xl border border-slate-700/70 bg-slate-900/50 backdrop-blur-sm p-6 text-left transition-all hover:border-slate-500 hover:bg-slate-900/80"
        >
          <div className={`mb-5 ${ECOSYSTEM_LOGO_SLOT_CLASS}`}>
            <img src={app.logo} alt={app.name} className={ecosystemLogoClass(app.name)} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{app.tagline}</p>
          <p className="text-sm text-slate-400 leading-relaxed mb-5 grow">{app.description}</p>
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-200 group-hover:text-white transition-colors">
            {app.current ? "You're here" : 'Launch app'}
            {!app.current && <ArrowUpRight size={16} className="text-slate-500 group-hover:text-teal-300 transition-colors" />}
          </span>
        </a>
      ))}
    </div>
  </div>
);

const FEATURES = [
  {
    title: 'Day-to-Night AI',
    description: 'Transform bright daytime exteriors into realistic dusk mood with balanced sky and lighting.',
    icon: <MoonStar size={18} />,
  },
  {
    title: 'Approval-First Workflow',
    description: 'Review and approve your favorite dusk version before any animation is generated.',
    icon: <Clock3 size={18} />,
  },
  {
    title: 'Style Variations',
    description: 'Regenerate multiple dusk looks using creative presets to match each listing.',
    icon: <Wand2 size={18} />,
  },
  {
    title: 'Before/After Timelapse',
    description: 'Create smooth transitions from the original photo to the approved dusk result.',
    icon: <ArrowRightLeft size={18} />,
  },
  {
    title: '5-10s Animation Controls',
    description: 'Select video length and motion style to fit reels, social clips, and listing ads.',
    icon: <Video size={18} />,
  },
  {
    title: 'Fast Delivery',
    description: 'Generate polished assets in minutes with cloud rendering and project history.',
    icon: <Sparkles size={18} />,
  },
];

interface LandingPageProps {
  onSignUp: () => void;
  onLogin: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onSignUp, onLogin }) => {
  const logoSrc = '/hyperdusk-logo-wordmark-alpha.png';

  return (
    <main className="relative z-10">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-900/30 backdrop-blur-sm border border-orange-400/30 text-orange-200 rounded-full text-sm font-medium mb-8">
            <Sparkles size={14} className="shrink-0" />
            ReHub Studio Hyper Dusk
          </div>
          <div className="flex justify-center mb-6">
            <img
              src={logoSrc}
              alt="Hyper Dusk"
              className="h-20 md:h-24 w-auto object-contain mix-blend-screen brightness-110 contrast-110 drop-shadow-[0_0_24px_rgba(255,155,125,0.2)]"
            />
          </div>
          <p className="text-xl text-slate-200 mb-10 max-w-2xl mx-auto">
            Convert listing photos from day to night, approve your best look, then generate cinematic twilight animations.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={onSignUp}
              className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-orange-500 to-fuchsia-500 hover:from-orange-400 hover:to-fuchsia-400 text-white rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-fuchsia-900/35 hover:shadow-orange-400/25 hover:scale-105 border border-orange-300/30"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-600 group-hover:rotate-[360deg] transition-transform duration-700">
                <Sparkles size={20} strokeWidth={2} />
              </span>
              Try Hyper Dusk Free
            </button>
            <button
              onClick={onLogin}
              className="px-8 py-5 bg-black/45 backdrop-blur-sm border-2 border-white/20 hover:border-orange-300/50 text-slate-100 hover:text-white rounded-2xl font-semibold text-lg transition-all"
            >
              Sign In
            </button>
          </div>
          <p className="text-sm text-slate-400 mt-4">
            Use your ReHub account. Shared credits across all ReHub apps.
          </p>

          <div className="mt-12 w-full max-w-3xl mx-auto rounded-2xl overflow-hidden border border-slate-700/80 shadow-2xl shadow-fuchsia-900/20">
            <div className="relative w-full pt-[56.25%] bg-black">
              <iframe
                src="https://www.youtube.com/embed/SQFR4MP0L2s?rel=0"
                title="Hyper Dusk Demo"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 text-left">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="p-6 bg-black/35 backdrop-blur-md border border-white/10 rounded-2xl hover:border-orange-300/30 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-orange-300 shrink-0">{feature.icon}</span>
                  <h3 className="text-lg font-semibold text-slate-100">{feature.title}</h3>
                </div>
                <p className="text-sm text-slate-300/85">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        <EcosystemGrid />
        <AskAiSection
          productName={ASK_AI_PRODUCT}
          prompt={ASK_AI_PROMPT}
          providers={ASK_AI_PROVIDERS}
          overviewHref="https://hyperdusk.studio/ask-ai"
          description="Ask ChatGPT, Perplexity, Grok, Claude or Gemini about HyperDusk’s day-to-dusk conversion, pricing and whether it fits your listing workflow."
        />
      </div>
    </main>
  );
};

export default LandingPage;
